import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { Text } from '../../components/index';

import { GroupCallController, OpenCallController } from '../../components/Call/CallController';
import { CallMemberCard, MessagesCard } from '../../components/Call/Card';
import Main from '../../components/VideoCall/main';

import socket from '../../socket';

import { alertError, alertSuccess } from '../../redux/stateActions';

import { GET_SESSION_STATUS } from '../../services/patient';

const GroupCall = ({ history }) => {
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));

    const dispatch = useDispatch();
    const { id } = useParams();
    const user = useSelector((state) => state.user);

    useEffect(() => {
        if (!id) {
            history.replace('/home');
        }
    }, [id]);

    /*--------------- Call Control Controller ---------------*/
    const roomInitRef = useRef();

    /*--------------- Get Session ---------------*/
    const [patientSummary, setPatientSummary] = useState({});
    const [meetData, setMeetData] = useState({});
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);

    const {
        data: getSessionData,
        error: getSessionError,
        refetch: getSessionRefetch,
    } = useQuery(GET_SESSION_STATUS, {
        variables: {
            id,
        },
        skip: _.isEmpty(user) || !id,
        pollInterval: 300000,
    });

    useEffect(() => {
        if (getSessionError) {
            dispatch(alertError(`Cannot Get Session: ${getSessionError.message}`));
        }

        if (getSessionData && !_.isEmpty(getSessionData.session)) {
            const { actualEndTime, sessionEnd, cancelled, patient_summaries, meet } =
                getSessionData.session;

            // check if video call end || check if session end
            if (cancelled) {
                dispatch(alertError(`Session Cancelled`));
                history.replace(`/appointment/${meet.type === 'group' ? 'group' : 'open'}/${id}`);
                return;
            }
            // check if video call end || check if session end
            if (actualEndTime || sessionEnd) {
                dispatch(alertSuccess(`Session Ended`));
                history.replace(`/appointment/${meet.type === 'group' ? 'group' : 'open'}/${id}`);
                return;
            }

            if (meet.type === 'group') {
                const foundPatientSummary = patient_summaries.find(
                    (s) => s.patient.id === user.patient.id
                );

                if (!foundPatientSummary) {
                    dispatch(alertError(`Client Summary not found`));
                    history.replace(`/appointment/group/${id}`);
                }

                setPatientSummary(foundPatientSummary);
            }

            if (meet.type === 'open') {
                setParticipants(
                    _.concat(
                        meet.staff,
                        meet.patients
                            .filter((patient) => patient.user !== null)
                            .map((patient) => patient),
                        meet.otherStaffs
                            .filter((staff) => staff.user !== null)
                            .map((staff) => staff)
                    )
                );
            }

            setMeetData(meet);
        }
    }, [getSessionError, getSessionData]);

    /*--------------- Member Window ---------------*/
    const [showMembers, setShowMembers] = useState(false);

    const handleCloseMemberts = () => {
        setShowMembers(false);
    };

    const handleParticipants = () => {
        setShowMembers(!showMembers);
        setShowMessages(false);
    };

    /*--------------- Messages Window ---------------*/
    const [showMessages, setShowMessages] = useState(false);
    const [messagesCount, setMessagesCount] = useState(0);

    const handleCloseMessages = () => {
        setShowMessages(false);
    };

    const handleMessages = () => {
        setShowMessages(!showMessages);
        setShowMembers(false);
    };

    /*--------------- End Call ---------------*/
    const endCallCallback = useCallback(() => {
        if (meetData.type === 'group') {
            socket.emit('patientLeaveGroupCall', {
                sessionId: id,
                patientSummaryId: patientSummary.id,
                patientId: patientSummary.patient.id,
            });
               localStorage.removeItem('isLoad');
            history.replace(`/appointment/group/${id}`);
        }

        if (meetData.type === 'open') {
            socket.emit('patientLeaveOpenCall', {
                sessionId: id,
                patientId: user.patient.id,
            });
       localStorage.removeItem('isLoad');
            history.replace(`/appointment/open/${id}`);
        }
    }, [patientSummary, meetData]);

    // group session socket
    useEffect(() => {
        if (meetData.type === 'group' && !_.isEmpty(patientSummary)) {
            // update join time
            socket.emit('patientJoinGroupCall', {
                sessionId: id,
                patientSummaryId: patientSummary.id,
                patientId: patientSummary.patient.id,
                staffId: meetData.staff.id,
            });

            // fire when reconnect after disconnect
            socket.on('connect', () => {
                socket.emit('reconnectGroupCall', {
                    sessionId: id,
                    patientSummaryId: patientSummary.id,
                    patientId: patientSummary.patient.id,
                });
            });

            // fire when provider click end call
            socket.on('providerEndGroupCall', (data) => {
                if (data.sessionId === id) {
                    getSessionRefetch();
                    roomInitRef.current.clientLeaveRoom(endCallCallback);
                }
            });

            // fire when history action
            history.listen(() => {
                socket.emit('patientLeaveGroupCall', {
                    sessionId: id,
                    patientSummaryId: patientSummary.id,
                    patientId: patientSummary.patient.id,
                });
            });
        }

        if (meetData.type === 'open') {
            // fire when provider click end call
            socket.on('providerEndOpenCall', (data) => {
                if (data.sessionId === id) {
                    // handleEndCall();
                    roomInitRef.current.clientLeaveRoom(endCallCallback);
                }
            });

            // fire when reconnect after disconnect
            socket.on('connect', () => {
                socket.emit('reconnectOpenCall', {
                    sessionId: id,
                    patientId: user.patient.id,
                });
            });
        }
    }, [patientSummary, meetData, roomInitRef]);

    useEffect(() => {
        // provider turn off mic
        socket.on('providerTurnPatientMicOff', (data) => {
            if (data.sessionId === id) {
                roomInitRef.current.trunClientMicOff();
            }
        });

        // provider turn off camera
        socket.on('providerTurnPatientCameraOff', (data) => {
            if (data.sessionId === id) {
                roomInitRef.current.turnClientWebcamOff();
            }
        });

        // fire after first render. for open session
        socket.emit('joinOpenSession', { sessionId: id, patientId: user.patient.id });

        socket.on('joinOpenSession', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });
        socket.on('patientLeaveOpenCall', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });
        socket.on('providerLeaveOpenCall', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });
    }, [roomInitRef]);

     useEffect(() => {
        console.log('meetData.type', meetData.type);
        if (meetData.type) {
            if (localStorage.getItem('isLoad') === null) {
                localStorage.setItem('isLoad', true);
                setLoading(false);
            } else {
                if (loading) {
                    console.log('aaaa');
                    if (meetData.type === 'group') {
                        socket.emit('patientLeaveGroupCall', {
                            sessionId: id,
                            patientSummaryId: patientSummary.id,
                            patientId: patientSummary.patient.id,
                        });
                        localStorage.removeItem('isLoad');
                        window.location.href = `/appointment/group/${id}`;
                    }
                    if (meetData.type === 'open') {
                        socket.emit('patientLeaveOpenCall', {
                            sessionId: id,
                            patientId: user.patient.id,
                        });
                        localStorage.removeItem('isLoad');
                        window.location.href = `/appointment/open/${id}`;
                    }
                }
            }
        }
    }, [meetData]);

//    useEffect(() => {
//     const handleBeforeUnload = async (event) => {
//          localStorage.removeItem('isLoad');
//         return true;
//     };
//        if (!loading) {
//     window.addEventListener('beforeunload', handleBeforeUnload);
//        }
//     return () => {
//         window.removeEventListener('beforeunload', handleBeforeUnload);
//     };
// }, []);

    return (
        <div
            style={{
                position: 'relative',
                backgroundColor: '#D2D6E9',
                paddingBottom: mdUp ? 0 : '90px',
                minHeight: '100vh',
            }}
        >
            <div
                id="activity-title"
                style={{
                    padding: '15px',
                    background:
                        'linear-gradient(180deg, #494E6A 1.16%, rgba(55, 60, 89, 0.541667) 49.49%, rgba(210, 214, 233, 0) 98.84%)',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1,
                }}
            >
                <Text $color="white" $ellipsis>{`Activity name - ${meetData?.name}`}</Text>
            </div>
            <div
                id="group-open-call-main-container"
                style={{
                    width: '100vw',
                }}
            >
                {!_.isEmpty(meetData) && (
                    <Main
                        ref={roomInitRef}
                        type={meetData?.type === 'group' ? 'group' : 'open'}
                        isProvider={false}
                        faceDetection={false}
                        patientId={patientSummary.patient?.id}
                        useMicCamera={meetData.type === 'group' || false}
                    />
                )}
            </div>

            {meetData.type === 'group' && (
                <GroupCallController
                    handleMic={() => roomInitRef.current.micControl()}
                    handleCamera={() => roomInitRef.current.webcamControl()}
                    onEndCall={() => roomInitRef.current.clientLeaveRoom(endCallCallback)}
                />
            )}
            {meetData.type === 'open' && (
                <OpenCallController
                    mdUp={mdUp}
                    onParticipants={handleParticipants}
                    participantsCount={participants.length}
                    onMessages={handleMessages}
                    messagesCount={messagesCount}
                    onEndCall={() => {
                        try {
                            roomInitRef.current.clientLeaveRoom(endCallCallback);
                        } catch (error) {
                            endCallCallback();
                        }
                    }}
                />
            )}

            <CallMemberCard
                open={showMembers}
                participants={participants}
                onClose={handleCloseMemberts}
            />
            {!_.isEmpty(meetData) && (
                <MessagesCard
                    open={showMessages}
                    participants={participants}
                    onClose={handleCloseMessages}
                    sender={user.patient.id}
                    user={user}
                    sessionId={id}
                    messagesCount={messagesCount}
                    setMessagesCount={setMessagesCount}
                />
            )}
        </div>
    );
};

GroupCall.propTypes = {
    history: PropTypes.object,
};

export default GroupCall;
